import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Mic, 
  TrendingUp, 
  Target, 
  Clock, 
  ArrowRight,
  BarChart3,
  Phone,
  Trophy,
  FileText,
  Shield,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import GuidedTour, { hasSeenTour } from "@/components/GuidedTour";

const TOUR_STEPS = [
  {
    target: '[data-testid="dashboard"]',
    title: "Welcome to Pitch Perfect!",
    description: "This is your command center. Track your progress, view recent sessions, and jump into practice anytime.",
    position: "bottom",
    icon: Sparkles,
  },
  {
    target: '[data-testid="start-roleplay-btn"]',
    title: "Start a Roleplay",
    description: "Click here to begin a live AI-powered voice call. Choose from presets, templates, or create custom scenarios.",
    position: "bottom",
    icon: Mic,
  },
  {
    target: '[data-testid="nav-objection-coach"]',
    title: "Objection Coach",
    description: "Got a tough objection? Type it in and get AI-generated strategies with exact responses you can use right away.",
    position: "right",
    icon: Shield,
  },
  {
    target: '[data-testid="nav-call-analysis"]',
    title: "Call Analysis",
    description: "Upload a recorded sales call and get detailed AI feedback with scoring across key sales metrics.",
    position: "right",
    icon: Phone,
  },
  {
    target: '[data-testid="nav-script-analysis"]',
    title: "Script Analysis",
    description: "Paste your sales script and get line-by-line feedback, an improved version, and metric scores.",
    position: "right",
    icon: FileText,
  },
  {
    target: '[data-testid="nav-leaderboard"]',
    title: "Leaderboard",
    description: "See how you stack up against your team. Track rankings and compete for the top spot.",
    position: "right",
    icon: Trophy,
  },
  {
    target: '[data-testid="nav-analytics"]',
    title: "Analytics",
    description: "Dive into your performance trends, skill breakdowns, and progress over time.",
    position: "right",
    icon: BarChart3,
  },
  {
    target: '[data-testid="avg-score-card"]',
    title: "You're all set!",
    description: "Your scores and stats will appear here as you complete sessions. Start your first roleplay to get going!",
    position: "bottom",
    icon: Target,
  },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, sessionsRes] = await Promise.all([
          fetch(`${API}/sessions/stats`, { credentials: "include" }),
          fetch(`${API}/sessions`, { credentials: "include" })
        ]);

        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats(statsData);
        }

        if (sessionsRes.ok) {
          const sessionsData = await sessionsRes.json();
          setRecentSessions(sessionsData.slice(0, 5));
        }
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Trigger tour for first-time users after dashboard loads
  useEffect(() => {
    if (!loading && !hasSeenTour()) {
      const timer = setTimeout(() => setShowTour(true), 600);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-yellow-500";
    return "text-red-500";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">
            Welcome back, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-muted-foreground mt-1">Ready to sharpen your sales skills?</p>
        </div>
        <Link to="/roleplay/new">
          <Button className="bg-primary hover:bg-primary/90 shadow-lg" data-testid="start-roleplay-btn">
            <Mic className="w-4 h-4 mr-2" /> Start Roleplay
          </Button>
        </Link>
      </div>

      {/* Stats Grid - Bento Style */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Main CTA Card */}
        <Card className="md:col-span-2 bg-gradient-primary text-white border-0 overflow-hidden relative">
          <div className="absolute inset-0 bg-glow opacity-50" />
          <CardContent className="p-6 relative z-10">
            <div className="flex items-start justify-between">
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                  <Mic className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold">Start a New Session</h3>
                  <p className="text-white/80 text-sm mt-1">
                    Practice cold calls, objection handling, or upselling
                  </p>
                </div>
                <Link to="/roleplay/new">
                  <Button variant="secondary" className="mt-2" data-testid="new-session-btn">
                    Begin Training <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="hidden sm:block">
                <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                    <Target className="w-8 h-8" />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Score Card */}
        <Card className="border-border" data-testid="avg-score-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-primary" />
              </div>
              <span className="text-sm text-muted-foreground">Avg Score</span>
            </div>
            <p className={`font-heading text-4xl font-bold ${getScoreColor(stats?.average_score || 0)}`}>
              {stats?.average_score || 0}
            </p>
            <p className="text-sm text-muted-foreground mt-1">out of 100</p>
          </CardContent>
        </Card>

        {/* Sessions Count */}
        <Card className="border-border" data-testid="total-sessions-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-blue-500" />
              </div>
              <span className="text-sm text-muted-foreground">Total Sessions</span>
            </div>
            <p className="font-heading text-4xl font-bold text-foreground">
              {stats?.total_sessions || 0}
            </p>
            <p className="text-sm text-muted-foreground mt-1">completed</p>
          </CardContent>
        </Card>

        {/* Skills Breakdown */}
        <Card className="md:col-span-2 border-border" data-testid="skills-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Skills Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {stats?.metrics_average && Object.keys(stats.metrics_average).length > 0 ? (
              Object.entries(stats.metrics_average).slice(0, 4).map(([key, value]) => (
                <div key={key} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground capitalize">
                      {key.replace(/_/g, " ")}
                    </span>
                    <span className={`font-medium ${getScoreColor(value)}`}>{value}%</span>
                  </div>
                  <Progress value={value} className="h-2" />
                </div>
              ))
            ) : (
              <p className="text-muted-foreground text-sm py-4 text-center">
                Complete a roleplay session to see your skill breakdown
              </p>
            )}
          </CardContent>
        </Card>

        {/* Recent Sessions */}
        <Card className="md:col-span-2 md:row-span-2 border-border" data-testid="recent-sessions-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                Recent Sessions
              </CardTitle>
              <Link to="/history">
                <Button variant="ghost" size="sm">View All</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[280px]">
              {recentSessions.length > 0 ? (
                <div className="space-y-3">
                  {recentSessions.map((session) => (
                    <Link 
                      key={session.session_id} 
                      to={`/roleplay/results/${session.session_id}`}
                      className="block"
                    >
                      <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${
                            session.status === "completed" ? "bg-green-500" :
                            session.status === "active" ? "bg-yellow-500" : "bg-gray-500"
                          }`} />
                          <div>
                            <p className="font-medium text-sm">{session.scenario_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatDate(session.started_at)}
                            </p>
                          </div>
                        </div>
                        {session.score && (
                          <span className={`font-mono font-bold ${getScoreColor(session.score)}`}>
                            {session.score}
                          </span>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Mic className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground text-sm">No sessions yet</p>
                  <Link to="/roleplay/new">
                    <Button variant="link" size="sm" className="mt-2">
                      Start your first roleplay
                    </Button>
                  </Link>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-border" data-testid="quick-actions-card">
          <CardContent className="p-6 space-y-3">
            <Link to="/analyze" className="block">
              <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <p className="font-medium">Analyze Call</p>
                  <p className="text-xs text-muted-foreground">Upload recording</p>
                </div>
              </div>
            </Link>
            <Link to="/scripts" className="block">
              <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="font-medium">Analyze Script</p>
                  <p className="text-xs text-muted-foreground">Improve your scripts</p>
                </div>
              </div>
            </Link>
          </CardContent>
        </Card>

        {/* Best Score */}
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Target className="w-5 h-5 text-green-500" />
              </div>
              <span className="text-sm text-muted-foreground">Best Score</span>
            </div>
            <p className="font-heading text-4xl font-bold text-green-500">
              {stats?.best_score || 0}
            </p>
            <p className="text-sm text-muted-foreground mt-1">personal best</p>
          </CardContent>
        </Card>
      </div>

      <GuidedTour
        steps={TOUR_STEPS}
        isOpen={showTour}
        onComplete={() => setShowTour(false)}
        tourKey="pitch_perfect_tour_seen"
      />
    </div>
  );
}
